import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    HelpOutline,
    AccountBalanceWalletOutlined,
    HomeOutlined,
    ReportProblemOutlined,
    EmojiPeopleOutlined,
    SettingsSuggestOutlined,
    CheckCircleOutline,
    AttachMoneyOutlined,
    GavelOutlined,
    PeopleAltOutlined,
    RoomPreferencesOutlined,
    InfoOutlined,
    ArrowForwardIos
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocList = [
    { id: "introduction", label: "はじめに・ルームシェアの魅力" },
    { id: "general-qa", label: "ルームシェア全般のQ&A" },
    { id: "contract-qa", label: "契約・費用に関するQ&A" },
    { id: "life-qa", label: "生活・トラブルのQ&A" },
    { id: "service-qa", label: "サービス利用のQ&A" },
    { id: "summary", label: "まとめ・ルームシェア成功の秘訣" }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3, pb: 6 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)} sx={{ ml: 2 }}>
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0, m: 0 }}>
                            {tocList.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32, minHeight: 32,
                                        bgcolor: '#f5f5f5', color: '#666', fontWeight: 'bold', fontSize: '1.1rem',
                                        borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.98rem', textAlign: 'left', py: 0.3 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの「Q&A」よくある質問20選
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、日本でも急速に広がりを見せる「ルームシェア」。家賃を抑えながら、生活をより豊かに、そして新しい人間関係を築けるスタイルとして人気です。しかし、「実際どんな暮らし？」「トラブルは？」「契約や費用は？」といった不安や疑問も多く聞かれます。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    この記事では、ルームシェアに関するよくある質問を20個厳選し、ジャンルごとに徹底解説。契約・費用、日常生活、トラブル、サービスの活用法まで、これさえ読めばルームシェアの「わからない」がすっきり解消できます。
                </Typography>
                <Box sx={{ my: 3 }}>

                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                    ※本記事は様々な経験談等に基づき執筆しています。初めての方も経験者も、ぜひ参考にしてください。
                </Typography>
            </Box>

            {/* ルームシェア全般のQ&A */}
            <Box id="general-qa" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <HelpOutline sx={{ mr: 1, verticalAlign: "middle" }} />
                    ルームシェア全般のQ&A
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの基礎知識や、初めての人が抱きがちな疑問を網羅的にまとめました。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <PeopleAltOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q1. ルームシェアとはどんな住まい方？
                            </Typography>
                            <Typography variant="body2">
                                一つの住宅を複数人で共同利用し、家賃や生活費を分担する住まい方です。リビングやキッチンなど共有スペースと、個室（または相部屋）を併用するケースが一般的。金銭面のメリットだけでなく、多様な交流や共同生活の楽しさも魅力です。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <EmojiPeopleOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q2. どんな人がルームシェアに向いている？
                            </Typography>
                            <Typography variant="body2">
                                コミュニケーション力があり、柔軟に物事を受け入れられる人。生活リズムや価値観の違いを尊重できる方に向いています。反面、自分の空間を絶対確保したい方や、音やプライバシーに敏感な方は慎重な検討が必要です。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <HomeOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q3. ルームシェアとファミリー向け物件はどう違う？
                            </Typography>
                            <Typography variant="body2">
                                ルームシェアは基本的に「血縁関係のない複数人」で住む形態で、各人が個別に賃料を負担します。一方、ファミリー向け物件は家族単位での入居を前提とし、世帯主が契約します。ルームシェアは個人間の取り決めが重要になります。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <RoomPreferencesOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q4. ルームシェアのメリット・デメリットは？
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="家賃・光熱費を節約できる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="孤独感が減り、友人ができやすい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <ReportProblemOutlined color="error" />
                                    </ListItemIcon>
                                    <ListItemText primary="プライバシーや生活リズムの違いでストレスが生じることも" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <SettingsSuggestOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q5. ルームメイトはどうやって探す？
                            </Typography>
                            <Typography variant="body2">
                                知人の紹介、SNSやルームシェア専門サイト、マッチングサービスなど、さまざまな方法があります。相性や生活スタイルを事前に確認するのが大切です。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <InfoOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q6. ルームシェア開始前に気をつけるべきことは？
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="入居条件や契約内容をしっかり確認" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="ルームメイトの性格や生活リズムを事前に把握" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="家事・費用分担ルールを決めておく" />
                                </ListItem>
                            </List>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <HelpOutline sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q7. ルームシェアは学生・社会人どちらが多い？
                            </Typography>
                            <Typography variant="body2">
                                都市部では学生・社会人問わず幅広い年代が利用しています。外国人やワーキングホリデーなど多国籍な住人も少なくありません。年齢よりも「価値観が合うか」がポイントです。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 契約・費用のQ&A */}
            <Box id="contract-qa" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <AccountBalanceWalletOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                    契約・費用に関するQ&A
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの契約形態、家賃・光熱費の分担、保証人や退去時の注意点など、お金と契約に関する疑問を徹底解説します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <AttachMoneyOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q8. 家賃・光熱費の分担はどう決める？
                            </Typography>
                            <Typography variant="body2">
                                一般的には人数割りですが、個室の広さや設備に差がある場合は相談調整が必要です。水道光熱費は実費を人数で割るパターンが多いですが、専有スペースが広ければ比率分担も検討しましょう。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <GavelOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q9. 契約は誰の名義で結ぶべき？
                            </Typography>
                            <Typography variant="body2">
                                代表者名義で賃貸契約し、他のメンバーは「同居人」とするケースが多いですが、全員連名契約できる物件もあります。責任やトラブル回避のため、契約形態は必ず全員で確認しましょう。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <AccountBalanceWalletOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q10. 敷金・礼金・保証人はどうなる？
                            </Typography>
                            <Typography variant="body2">
                                通常の賃貸と同様に必要ですが、保証人は代表者1名で可の場合や、全員分求められる場合など物件によって異なります。契約前に管理会社やオーナーに必ず確認しましょう。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <GavelOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q11. ルームシェア契約書は必要？
                            </Typography>
                            <Typography variant="body2">
                                口約束やLINEだけの合意はトラブルの元。賃貸契約書とは別に、住人同士で「ルームシェア契約書」を作成するのが安心です。家賃・光熱費の分担、家事分担、退去時精算方法なども明記しましょう。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <AttachMoneyOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q12. 退去時の精算・トラブル対応は？
                            </Typography>
                            <Typography variant="body2">
                                退去時は事前に精算ルールを決めておくことが肝心。敷金の返還、共用の家具家電の処分、未払い費用の分担など、口頭だけでなく文書で残しておきましょう。第三者の仲裁も活用できます。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <AccountBalanceWalletOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q13. ルームシェアの初期費用はどのくらい？
                            </Typography>
                            <Typography variant="body2">
                                敷金・礼金・仲介手数料・初回家賃・家具家電購入費など。一般的な一人暮らしより抑えられますが、契約形態や物件条件で変動します。初期費用のシミュレーションは事前に必ず行いましょう。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/money-calc_roomshare-qa-100.jpg"
                        alt="テーブルの上に家計簿や計算機、複数の手が集まり家賃や光熱費の分担について話し合っているルームシェア住人たちの様子。明るい自然光とアットホームな雰囲気"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0px 4px 16px rgba(0,0,0,0.08)' }}
                    />
                </Box>
            </Box>

            {/* 生活・トラブルのQ&A */}
            <Box id="life-qa" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <HomeOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                    生活・トラブルのQ&A
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    共同生活だからこそ起こりやすい「ちょっとした困りごと」や「気まずい問題」の解決法、日常生活での工夫をQ&A形式でご紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <SettingsSuggestOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q14. 家事の分担はどうすればうまくいく？
                            </Typography>
                            <Typography variant="body2">
                                「当番制」や「気付いた人がやる」など方式はさまざまですが、曖昧だと不満の元。ホワイトボードやアプリで担当を見える化し、定期的に話し合うのがコツです。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <ReportProblemOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q15. トラブルになりやすいポイントは？
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <ReportProblemOutlined color="warning" />
                                    </ListItemIcon>
                                    <ListItemText primary="生活音（音楽・テレビ・話し声）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <ReportProblemOutlined color="warning" />
                                    </ListItemIcon>
                                    <ListItemText primary="掃除・ゴミ出しの分担" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <ReportProblemOutlined color="warning" />
                                    </ListItemIcon>
                                    <ListItemText primary="冷蔵庫など共用物の使い方" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <ReportProblemOutlined color="warning" />
                                    </ListItemIcon>
                                    <ListItemText primary="金銭の立て替え・清算の曖昧さ" />
                                </ListItem>
                            </List>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <EmojiPeopleOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q16. ルームメイトとの信頼関係を築くには？
                            </Typography>
                            <Typography variant="body2">
                                「ありがとう」「ごめんね」を素直に伝える、小さな約束も守る、感情をため込まず定期的に話し合う。共同生活は小さな配慮の積み重ねが絆を深めます。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <PeopleAltOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q17. プライバシーはどう守ればいい？
                            </Typography>
                            <Typography variant="body2">
                                個室がある物件を選ぶ、共有スペースの使い方ルールを明確にする、訪問者の有無を事前に伝えるなど。お互いの「距離感」を尊重することが大切です。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <ReportProblemOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q18. トラブルが起きた時の解決法は？
                            </Typography>
                            <Typography variant="body2">
                                まずは冷静に話し合い、感情的にならないこと。第三者を頼るのも有効です。解決できない場合、退去や契約の見直しも選択肢となります。
                            </Typography>
                        </Paper>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <SettingsSuggestOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q19. 食事や買い物はどうしている？
                            </Typography>
                            <Typography variant="body2">
                                各自で用意するパターンが多いですが、調味料や日用品は共同購入にすると経済的。週に1回は「みんなでご飯会」など、楽しみを作るのもおすすめです。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/share-cooking_roomshare-qa-100.jpg"
                        alt="広々としたキッチンでルームメイトたちが協力して食事を準備し、和やかな雰囲気で笑顔が弾ける共同生活のワンシーン。明るい照明と清潔感が特徴"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0px 4px 16px rgba(0,0,0,0.08)' }}
                    />
                </Box>
            </Box>

            {/* サービス利用のQ&A */}
            <Box id="service-qa" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <SettingsSuggestOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                    サービス利用のQ&A
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアをより快適・安全に進めるためのサポートサービスや、最近注目のマッチングサイト・アプリ活用法についてまとめました。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <InfoOutlined sx={{ mr: 1, verticalAlign: "middle" }} />
                                Q20. ルームシェア専用のマッチングサイトは安全？
                            </Typography>
                            <Typography variant="body2">
                                本人確認やレビュー機能、トラブル時の運営サポートが充実しているサイトを選びましょう。やり取りはアプリ内メッセージで行い、個人情報の開示は慎重に。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <CheckCircleOutline sx={{ mr: 1, verticalAlign: "middle" }} />
                    まとめ・ルームシェア成功の秘訣
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは「人」と「ルール」がキーポイント。しっかり話し合い、信頼関係を築くことで、思わぬトラブルも防げます。Q&Aで紹介したポイントを意識して、あなたのルームシェア生活が快適で実り多いものになるよう願っています！
                </Typography>
                <Box sx={{ my: 3 }}>

                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                    この20のQ&Aはルームシェアの一部に過ぎません。状況に応じて柔軟に対応し、安心で楽しい共同生活を送りましょう。
                </Typography>
                <Box sx={{ mt: 3 }}>
                    <Chip label="ルームシェア" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="契約・費用" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="トラブル対策" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="サービス活用" color="primary" variant="outlined" />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-qa-20',
    slug: 'roomshare-qa-20',
    title: 'ルームシェアの「Q&A」よくある質問20選',
    excerpt: 'ルームシェアにまつわる基礎知識から契約・費用、生活トラブル、サービス活用法まで、よくある質問20個を専門的に解説します。',
    coverImage: '/images/blog/cover-shareliving_roomshare-qa-100.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。リラックスした雰囲気と温かみのあるインテリアが印象的で、記事全体のテーマである「快適な共同生活」を象徴している。',
    publishedAt: '2024-06-21T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '暮らしの知恵', '契約・費用'],
    tags: ['ルームシェア', 'Q&A', 'トラブル対策', 'マッチングサービス', '生活術'],
    author: {
        name: '田中 宏樹',
        avatar: '/images/avatars/roomshare-expert.jpg',
        bio: '暮らしと住まいに関する取材・執筆歴15年。ルームシェアサポートや生活アドバイスの経験多数。住まいの専門メディアでコラム・Q&Aを担当。',
        profilePicture: '/images/avatars/roomshare-expert.jpg'
    },
    viewCount: 1984,
    contentComponent: PostContent
};

export default post;